import React, { useState, useEffect, useRef } from 'react';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { useSetupIntentPopup } from '../../hooks/useSetupIntentPopup';
import type { StripeError } from './StripePopupShared';
import {
    assertSuccessResponse,
    assertErrorResponse,
} from '~/modules/root/graphql/responses';
import { PAYMENT_ALREADY_COMPLETED_MESSAGE } from '../../type/types';
import {
    buildElementsOptions,
    useStripeInstance,
    useBackButtonClose,
    StripePaymentForm,
    StripePopupDialog,
} from './StripePopupShared';
import { buildProposalStripeReturnUrl } from '../../util/proposal-payment-confirm';

/** Extended Stripe type with confirmSetup method for setup intents */
interface StripeWithConfirmSetup extends Stripe {
    confirmSetup(options: {
        elements: StripeElements;
        confirmParams: { return_url: string };
        redirect: 'if_required';
    }): Promise<{ error?: StripeError }>;
}

/**
 * Stripe setup intent popup component with payment element.
 * Creates a setup intent via API and renders the shared Stripe popup dialog.
 */
export const StripeSetupIntentPopup: React.FC<{
    onSetupSuccess?: () => void;
    onSetupError?: (error: string) => void;
    onConfirmFallback?: () => void;
}> = ({ onSetupSuccess, onSetupError, onConfirmFallback }) => {
    const {
        isSetupIntentPopupOpen,
        closePopup,
        currentCustomerId,
        proposalUrlSegment,
        stripeAccount,
        paymentData,
        invoicePaymentId,
        publishableKey,
    } = useSetupIntentPopup();

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    /** Error message to display when setup intent creation fails */
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasCreatedSetupIntent = useRef(false);

    const stripePromise = useStripeInstance(stripeAccount, publishableKey);

    // Create setup intent via API when popup opens
    useEffect(() => {
        if (
            isSetupIntentPopupOpen &&
            currentCustomerId &&
            !hasCreatedSetupIntent.current
        ) {
            setIsLoading(true);
            hasCreatedSetupIntent.current = true;

            const createSetupIntentViaAPI = async () => {
                try {
                    const formData = new FormData();
                    formData.append('contactId', String(currentCustomerId));
                    if (invoicePaymentId) {
                        formData.append('invoicePaymentId', invoicePaymentId);
                    }
                    // What this page is showing, so the server can refuse a stale tab.
                    if (paymentData?.amount !== undefined) {
                        formData.append(
                            'displayedAmount',
                            String(paymentData.amount),
                        );
                    }

                    const response = await fetch(
                        '/remix-api/setup-intent/create',
                        {
                            method: 'POST',
                            body: formData,
                        },
                    );

                    const result = await response.json();

                    if (assertSuccessResponse(result) && result.data) {
                        const data = result.data as {
                            clientSecret: string;
                            id: string;
                        };
                        setClientSecret(data.clientSecret);
                        setSetupIntentId(data.id);
                        setErrorMessage(null);
                        setIsLoading(false);
                    } else {
                        const error = assertErrorResponse(result)
                            ? String(result.message)
                            : 'Failed to create setup intent';
                        if (error.includes(PAYMENT_ALREADY_COMPLETED_MESSAGE)) {
                            setIsLoading(false);
                            closePopup();
                            onSetupSuccess?.();
                            return;
                        }
                        setErrorMessage(error);
                        onSetupError?.(error);
                        setIsLoading(false);
                    }
                } catch (err) {
                    const error =
                        err instanceof Error
                            ? err.message
                            : 'Failed to create setup intent';
                    setErrorMessage(error);
                    onSetupError?.(error);
                    setIsLoading(false);
                }
            };

            createSetupIntentViaAPI();
        } else if (!isSetupIntentPopupOpen) {
            // Reset when popup closes
            setClientSecret(null);
            setSetupIntentId(null);
            setIsLoading(false);
            setErrorMessage(null);
            hasCreatedSetupIntent.current = false;
        }
    }, [
        isSetupIntentPopupOpen,
        currentCustomerId,
        onSetupError,
        onSetupSuccess,
        closePopup,
        invoicePaymentId,
        paymentData,
    ]);

    useBackButtonClose(isSetupIntentPopupOpen, closePopup, 'setupIntentPopup');

    const options = buildElementsOptions(clientSecret);

    /** Confirm the setup intent using Stripe.js */
    const confirmSetup = async (
        stripe: Stripe,
        elements: StripeElements,
    ): Promise<{ error?: StripeError; stripeIntentId?: string }> => {
        const returnUrl =
            invoicePaymentId != null
                ? buildProposalStripeReturnUrl({
                      invoicePaymentId,
                      intentType: 'setup',
                  })
                : window.location.href;

        const result = await (stripe as StripeWithConfirmSetup).confirmSetup({
            elements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required',
        });

        return {
            ...result,
            stripeIntentId: setupIntentId ?? undefined,
        };
    };

    return (
        <StripePopupDialog
            isOpen={isSetupIntentPopupOpen}
            closePopup={closePopup}
            paymentData={paymentData ?? undefined}
            isLoading={isLoading}
            loadingMessage="Processing payment method..."
            errorMessage={errorMessage}
            stripePromise={stripePromise}
            options={options}
            stripeAccount={stripeAccount}
        >
            <StripePaymentForm
                confirmAction={confirmSetup}
                invoicePaymentId={invoicePaymentId}
                proposalConfirm={
                    proposalUrlSegment && invoicePaymentId
                        ? {
                              proposalUrlSegment,
                              intentType: 'setup',
                              stripeIntentId: setupIntentId,
                          }
                        : undefined
                }
                onConfirmFallback={onConfirmFallback}
                closePopup={closePopup}
                successMessage="Payment method saved successfully!"
                failureMessage="Setup failed"
                formId="setup-form"
                onSuccess={() => {
                    closePopup();
                    onSetupSuccess?.();
                }}
                onError={(error) => {
                    onSetupError?.(error);
                }}
            />
        </StripePopupDialog>
    );
};
